import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlaggedList } from '@/components/capture/FlaggedList';
import { rejectionText } from '@/components/jobs/job-text';
import { listCaptures } from '@/lib/api';
import type { CaptureRecord } from '@/lib/api/types';

/**
 * CA-08: My flagged captures (wireframes/capture-app.html #ca08). Three
 * kinds of capture need the operator, and this screen lists all of them.
 *
 * A manager asked for a re-capture. Those come from the API
 * (`GET /capture?status=RECAPTURE_REQUESTED`), each opening on the shared
 * review route where the operator retakes the frame. This half used to be
 * the wireframe's structure with no data behind it, so a manager's request
 * reached no one (run 1 finding 11-003).
 *
 * A capture was rejected, by a reviewer or by an auto-reject rule
 * (`GET /capture?status=REJECTED`). Each row names the reason from the
 * catalogue, with Auto when a rule rejected it (INTERFACES.md section 3),
 * so the operator learns why at once rather than finding a red status on
 * the job.
 *
 * An upload failed on the device. Those come from the offline queue
 * (`FlaggedList`), with retry and discard.
 */
export function FlaggedCaptures() {
  const [recaptures, setRecaptures] = useState<CaptureRecord[] | null>(null);
  const [rejected, setRejected] = useState<CaptureRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    function load(status: 'RECAPTURE_REQUESTED' | 'REJECTED', set: (items: CaptureRecord[]) => void) {
      listCaptures({ status })
        .then((page) => {
          if (!cancelled) set(page.items);
        })
        .catch((cause: unknown) => {
          if (!cancelled) {
            setError(cause instanceof Error ? cause.message : 'Could not load flagged captures');
            set([]);
          }
        });
    }
    load('RECAPTURE_REQUESTED', setRecaptures);
    load('REJECTED', setRejected);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex h-[calc(100svh-9rem)] min-h-64 flex-col gap-4 lg:h-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold">Flagged for you</h1>
        <Badge tone="warning">Needs attention</Badge>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto lg:overflow-visible">

        {error && <p className="text-sm text-warning">{error}</p>}

        <section aria-label="Re-capture requests" className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold">Retake requests</h2>
          {recaptures === null && <p className="text-sm text-muted">Loading…</p>}
          {recaptures !== null && recaptures.length === 0 && !error && (
            <p className="text-sm text-muted">No retake requests</p>
          )}
          {recaptures !== null && recaptures.length > 0 && (
            <ul className="flex flex-col gap-2">
              {recaptures.map((capture) => (
                <li key={capture.captureId}>
                  <CaptureRow capture={capture} note={recaptureReason(capture)} badge={<Badge tone="warning">Retake</Badge>} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Rejected captures" className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold">Rejected</h2>
          {rejected === null && <p className="text-sm text-muted">Loading…</p>}
          {rejected !== null && rejected.length === 0 && !error && (
            <p className="text-sm text-muted">No rejected captures</p>
          )}
          {rejected !== null && rejected.length > 0 && (
            <ul className="flex flex-col gap-2">
              {rejected.map((capture) => (
                <li key={capture.captureId}>
                  <CaptureRow
                    capture={capture}
                    note={capture.rejection?.detail ?? null}
                    badge={
                      <Badge tone="danger">{capture.rejection ? rejectionText(capture.rejection) : 'Rejected'}</Badge>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Upload failures" className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold">Failed uploads</h2>
          <FlaggedList />
        </section>
      </div>
    </div>
  );
}

/** The reason the manager gave, from the latest re-capture verdict on the trail. */
function recaptureReason(capture: CaptureRecord): string | null {
  const event = [...(capture.reviewTrail ?? [])].reverse().find((item) => item.verdict === 'request_recapture');
  return event?.note ?? null;
}

function CaptureRow({ capture, note, badge }: { capture: CaptureRecord; note: string | null; badge: ReactNode }) {
  const description = capture.fields?.description?.value;
  return (
    <Link
      to={`/capture/${encodeURIComponent(capture.captureId)}/result`}
      className="flex min-h-11 items-center gap-3 rounded-card border border-border p-3 text-sm hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="break-words">{description ? String(description) : 'Unnamed item'}</span>
        {note && <span className="text-xs text-muted">&ldquo;{note}&rdquo;</span>}
        <span className="text-xs text-muted">
          Captured {new Date(capture.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
        </span>
      </span>
      <span className="shrink-0">{badge}</span>
      <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden="true" />
    </Link>
  );
}
